import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '@/components/landing/Header';

const PrivacyPolicyPage = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Your Song Request</title>
        <meta name="description" content="Privacy policy for Your Song Request platform." />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-200">
        <Header />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-gray-800 rounded-lg shadow-xl p-8 md:p-12">
            <h1 className="text-4xl font-bold text-white mb-8">Privacy Policy</h1>
            
            <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">1. Data Collection</h2>
                <p className="leading-relaxed mb-4">
                  We collect minimal data necessary to run the service: account details, song requests, and information needed to process payments and payouts. We do <strong>not</strong> store full card numbers or full authentication credentials for payment providers on our own servers.
                </p>
                <p className="leading-relaxed">
                  <strong>United Kingdom (artists, audience, and account holders):</strong> card and related payment data are processed by our partners <strong>A2B</strong> and <strong>Cardstream</strong> (or their subprocessors as applicable). Those partners collect and process payment data in line with their own privacy notices and PCI-DSS obligations.
                </p>
                <p className="leading-relaxed mt-4">
                  <strong>United States, Canada, Ireland, the Eurozone, Australia, and New Zealand (artists, audience, and account holders):</strong> payments are processed by <strong>PayPal</strong> (or as otherwise shown at checkout) in the artist&apos;s local currency. PayPal’s privacy policy applies to information you provide to PayPal.
                </p>
                <p className="leading-relaxed mt-4">
                  For other supported regions, the payment provider shown at checkout or in your account applies; we only receive limited transaction metadata needed to operate the platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">2. How We Use Your Data</h2>
                <p className="leading-relaxed mb-4">
                  Your data is used solely to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li className="leading-relaxed">Process song request fees and artist payouts according to your region and chosen payment method.</li>
                  <li className="leading-relaxed">Display your request name or message to the artist during live sessions.</li>
                  <li className="leading-relaxed">Comply with legal, accounting, and tax obligations.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">3. Data Security</h2>
                <p className="leading-relaxed">
                  We use industry-standard encryption (such as TLS) to protect data in transit. Card payments for the United Kingdom are handled by PCI-DSS compliant infrastructure (A2B / Cardstream). Payments via PayPal (United States, Canada, Ireland, the Eurozone, Australia, and New Zealand) are subject to PayPal’s security and compliance programmes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mt-8 mb-4">4. Contact Us</h2>
                <p className="leading-relaxed">
                  For any privacy concerns, please contact:{' '}
                  <a 
                    href="mailto:support@yoursongrequest.com" 
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    support@yoursongrequest.com
                  </a>
                </p>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-700">
              <Link 
                to="/" 
                className="text-blue-400 hover:text-blue-300 underline text-sm"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-black border-t border-gray-800 py-8 mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">
                © 2026 Your Song Request. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
                <Link to="/refund-policy" className="hover:text-gray-400 transition-colors">
                  Refund Policy
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/terms-of-service" className="hover:text-gray-400 transition-colors">
                  Terms & Conditions
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/privacy-policy" className="hover:text-gray-400 transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-800 text-center">
              <p className="text-xs text-gray-600">
                Registered Business Address: 6 Pendle Close, Thornton-Cleveley's, FY5 3WB
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
